/**
 * Unlock via your Mac (docs/e2e.md "Unlock via a machine"): a trusted, endorsed browser that is
 * content-locked and whose passkey has no PRF asks a key-holding daemon of the account for the
 * current epoch keys. The request needs no secret, only authentication:
 *
 * - it is bound to the browser's endorsed device key (ECDSA signature) and to the browser's ECDH
 *   key the daemon wraps to;
 * - it carries a FRESH passkey assertion (user verification required) whose challenge is the
 *   request digest, so it can't be replayed for another request, device or key;
 * - it names the directory head the browser verified, a random nonce and an expiry of at most
 *   5 minutes.
 *
 * The daemon (a directory member that holds the current key) verifies everything against its own
 * pinned passkeys, endorsements, revocations and verified directory checkpoint, then wraps each
 * epoch key it holds to the request's ECDH key. The browser installs a key only after checking it
 * against the ADK commitment of its own verified directory, then self-joins with the existing ADK
 * possession proof. The relay sees ids, public keys, signatures and wrapped keys only.
 */
import { approvalChallengeFor, UNLOCK_REQUEST_BATCH_DOMAIN, } from './approval-batch.js';
import { fromBase64Url, randomBytes, toBase64Url } from './bytes.js';
import { exactKeys, positiveInteger, validId } from './content.js';
import { domainSeparated, hashCanonical, hashCanonicalB64 } from './hash.js';
import { jwkThumbprint, signP256, verifyP256 } from './keys.js';
import { isWebAuthnAssertion } from './webauthn.js';
import { importAgreementPublicKey, unwrapAccountKey, wrapAccountKey, } from './wrapping.js';
export const UNLOCK_REQUEST_DOMAIN = UNLOCK_REQUEST_BATCH_DOMAIN;
/** A request lives at most this long (and the daemon remembers its digest at least as long). */
export const UNLOCK_REQUEST_MAX_TTL_MS = 5 * 60 * 1000;
const CLOCK_SKEW_MS = 60_000;
export const UNLOCK_REFUSALS = [
    'malformed',
    'wrong-account',
    'expired',
    'lifetime',
    'replayed',
    'unknown-device',
    'revoked-device',
    'expired-device',
    'session-device',
    'bad-signature',
    'bad-assertion',
    'untrusted-credential',
    'revoked-credential',
    'recovery-hold',
    'quarantined',
    'key-mismatch',
    'stale-key',
    'directory-fork',
    'no-key',
];
export async function createUnlockRequestStatement(input) {
    const issuedAt = input.now ?? Date.now();
    const ttl = Math.min(input.ttlMs ?? UNLOCK_REQUEST_MAX_TTL_MS, UNLOCK_REQUEST_MAX_TTL_MS);
    await importAgreementPublicKey(input.agreementKey);
    return {
        v: 1,
        type: 'viko.unlock-request',
        accountId: input.accountId,
        deviceKeyId: input.deviceKeyId,
        agreementKey: {
            kty: 'EC',
            crv: 'P-256',
            x: input.agreementKey.x,
            y: input.agreementKey.y,
        },
        directory: { ...input.directory },
        nonce: toBase64Url(randomBytes(32)),
        issuedAt,
        expiresAt: issuedAt + ttl,
    };
}
/** The request digest: the passkey challenge, and the id daemons and the relay bind to. */
export function unlockRequestChallenge(statement) {
    return hashCanonical(UNLOCK_REQUEST_DOMAIN, statement);
}
export function unlockRequestDigest(statement) {
    return hashCanonicalB64(UNLOCK_REQUEST_DOMAIN, statement);
}
/** Device-key signature; the caller obtains the passkey assertion over the same digest. */
export async function signUnlockRequest(statement, device, assertion, batch) {
    return {
        statement: structuredClone(statement),
        signature: toBase64Url(await signP256(device.privateKey, domainSeparated(UNLOCK_REQUEST_DOMAIN, statement))),
        assertion,
        ...(batch ? { batch: structuredClone(batch) } : {}),
    };
}
function fail(reason, detail) {
    return detail === undefined ? { ok: false, reason } : { ok: false, reason, detail };
}
function wellFormed(signed) {
    try {
        exactKeys(signed, signed.batch === undefined
            ? ['statement', 'signature', 'assertion']
            : ['statement', 'signature', 'assertion', 'batch']);
        const s = signed.statement;
        exactKeys(s, [
            'v',
            'type',
            'accountId',
            'deviceKeyId',
            'agreementKey',
            'directory',
            'nonce',
            'issuedAt',
            'expiresAt',
        ]);
        exactKeys(s.agreementKey, ['kty', 'crv', 'x', 'y']);
        exactKeys(s.directory, ['revision', 'digest', 'epoch']);
        if (s.v !== 1 || s.type !== 'viko.unlock-request')
            return false;
        validId(s.accountId);
        validId(s.deviceKeyId);
        if (typeof s.nonce !== 'string' || fromBase64Url(s.nonce).length !== 32)
            return false;
        if (!Number.isSafeInteger(s.issuedAt) || !Number.isSafeInteger(s.expiresAt))
            return false;
        const d = s.directory;
        if (!Number.isSafeInteger(d.revision) || d.revision < 0)
            return false;
        if (!Number.isSafeInteger(d.epoch) || d.epoch < 0)
            return false;
        if (d.revision === 0 ? d.digest !== null : typeof d.digest !== 'string')
            return false;
        if (typeof d.digest === 'string' && fromBase64Url(d.digest).length !== 32)
            return false;
        if (typeof signed.signature !== 'string' || fromBase64Url(signed.signature).length !== 64)
            return false;
        return isWebAuthnAssertion(signed.assertion);
    }
    catch {
        return false;
    }
}
/**
 * Everything a key-holding endpoint checks before it wraps a key to the requester. Order matters
 * only for which reason is reported; every check must pass.
 */
export async function verifyUnlockRequest(input, o) {
    const now = o.now ?? Date.now();
    // Snapshot before the first await: untrusted callers cannot mutate what gets verified.
    let signed;
    try {
        signed = structuredClone(input);
    }
    catch {
        return fail('malformed');
    }
    if (!signed || typeof signed !== 'object' || !wellFormed(signed))
        return fail('malformed');
    const s = signed.statement;
    try {
        await importAgreementPublicKey(s.agreementKey);
    }
    catch {
        return fail('malformed', 'agreement key');
    }
    if (s.accountId !== o.accountId)
        return fail('wrong-account');
    if (s.expiresAt <= s.issuedAt || s.expiresAt - s.issuedAt > UNLOCK_REQUEST_MAX_TTL_MS)
        return fail('lifetime');
    if (s.issuedAt > now + CLOCK_SKEW_MS || now > s.expiresAt)
        return fail('expired');
    // The device: endorsed by a pinned passkey, live, not revoked, not "Just this session".
    const lookup = o.trust.lookupDevice(s.deviceKeyId, now);
    if (lookup.status === 'unknown')
        return fail('unknown-device');
    if (lookup.status === 'revoked')
        return fail('revoked-device', lookup.detail);
    if (lookup.status === 'expired')
        return fail('expired-device');
    const device = lookup.device;
    if (device.session)
        return fail('session-device');
    const key = device.publicKey;
    if (!(await verifyP256(key, fromBase64Url(signed.signature), domainSeparated(UNLOCK_REQUEST_DOMAIN, s))))
        return fail('bad-signature');
    // A fresh passkey tap bound to exactly this request (challenge = request digest, UV required).
    let challenge;
    try {
        challenge = await approvalChallengeFor(UNLOCK_REQUEST_DOMAIN, await unlockRequestChallenge(s), signed.batch);
    }
    catch {
        return fail('bad-assertion', 'batch');
    }
    const approval = await o.trust.verifyPasskeyApproval(signed.assertion, challenge);
    if (!approval.ok) {
        if (approval.reason === 'untrusted-credential')
            return fail('untrusted-credential');
        if (approval.reason === 'revoked-credential')
            return fail('revoked-credential');
        return fail('bad-assertion', approval.reason);
    }
    const credentialId = approval.credential.credentialId;
    // Hold rules are unchanged: a passkey made during a recovery (or a device it endorsed) waits.
    const held = new Set(o.heldCredentials ?? []);
    if (held.has(credentialId) || held.has(device.endorsedBy))
        return fail('recovery-hold');
    // The verifier's own head: it must be at least as new as the browser's, on the same chain.
    const c = o.checkpoint;
    if (!c)
        return fail('stale-key');
    if (s.directory.revision > c.revision || s.directory.epoch > c.statement.epoch)
        return fail('stale-key');
    if (s.directory.revision === c.revision && s.directory.digest !== c.digest)
        return fail('directory-fork');
    if (c.unproven?.includes(s.deviceKeyId))
        return fail('quarantined');
    const listed = c.statement.recipients.find((r) => r.id === s.deviceKeyId);
    if (listed) {
        if (listed.type !== 'device')
            return fail('key-mismatch');
        if ((await jwkThumbprint(listed.publicKey)) !== (await jwkThumbprint(s.agreementKey)))
            return fail('key-mismatch');
    }
    const digest = await unlockRequestDigest(s);
    if (o.nonces) {
        const check = o.nonces.checkAndAdd(`unlock:${digest}`, s.expiresAt + CLOCK_SKEW_MS + UNLOCK_REQUEST_MAX_TTL_MS, now);
        if (check !== 'fresh')
            return fail('replayed', check);
    }
    return { ok: true, digest, device, credentialId };
}
/**
 * Wrap each epoch key the fulfiller holds to the request's ECDH key, bound to the account, epoch
 * and the requesting device id. The caller must have verified the request first.
 */
export async function createUnlockGrant(request, requestDigest, head, keys) {
    if (!keys.some((k) => k.epoch === head.statement.epoch))
        throw new Error('no current key');
    const wraps = [];
    for (const k of [...keys].sort((a, b) => a.epoch - b.epoch)) {
        positiveInteger(k.epoch);
        if (k.epoch > head.statement.epoch)
            continue;
        wraps.push({
            epoch: k.epoch,
            wrap: await wrapAccountKey(k.adk, {
                version: 1,
                accountId: request.accountId,
                epoch: k.epoch,
                recipientId: request.deviceKeyId,
            }, request.agreementKey),
        });
    }
    return {
        v: 1,
        requestDigest,
        deviceKeyId: request.deviceKeyId,
        directory: {
            revision: head.revision,
            digest: head.digest,
            epoch: head.statement.epoch,
        },
        wraps: wraps.slice(-64),
    };
}
/**
 * Browser: unwrap a grant for the request it made. Raw keys are returned for commitment checks
 * against the browser's own verified directory (the caller MUST check and zero them).
 */
export async function openUnlockGrant(grant, expect) {
    if (grant.v !== 1 || grant.requestDigest !== expect.requestDigest)
        throw new Error('unlock grant for another request');
    if (grant.deviceKeyId !== expect.deviceKeyId)
        throw new Error('unlock grant for another device');
    if (!Array.isArray(grant.wraps) || !grant.wraps.length || grant.wraps.length > 64)
        throw new Error('invalid unlock grant');
    const out = [];
    const seen = new Set();
    try {
        for (const w of grant.wraps) {
            positiveInteger(w.epoch);
            if (seen.has(w.epoch))
                throw new Error('invalid unlock grant');
            seen.add(w.epoch);
            out.push({
                epoch: w.epoch,
                adk: await unwrapAccountKey(w.wrap, {
                    version: 1,
                    accountId: expect.accountId,
                    epoch: w.epoch,
                    recipientId: expect.deviceKeyId,
                }, expect.privateKey, expect.publicJwk),
            });
        }
    }
    catch (e) {
        for (const k of out)
            k.adk.fill(0);
        throw e;
    }
    return out;
}
//# sourceMappingURL=unlock-request.js.map